import { computed, inject, Injectable, Injector, signal } from '@angular/core';
import { fetchAndActivate, getValue, RemoteConfig } from '@angular/fire/remote-config';
import { isSupported } from 'firebase/remote-config';
import { environment } from 'src/environments/environment';

const KEY_PHOTOS_MAXIMUM_EXTERNAL_DROP_WEB = 'photos_maximum_external_drop_web';
const KEY_REPORT_DISABLED_CATEGORIES = 'report_disabled_categories';
const KEY_REPORT_XLSX = 'report_xlsx';
const KEY_THEMES_COMPACT = 'themes_compact';
const KEY_FLOOR_PLANS_WEB = 'floor_plans_web';
const REMOTE_CONFIG_PRODUCTION_FETCH_INTERVAL_MS = 12 * 60 * 60 * 1000;
const REMOTE_CONFIG_NON_PRODUCTION_FETCH_INTERVAL_MS = 60 * 1000;
const REMOTE_CONFIG_FETCH_TIMEOUT_MS = 10 * 1000;

type EnvironmentWithRemoteConfigOverride = typeof environment & {
  remoteConfigMinimumFetchIntervalMillis?: number;
};

export type FloorPlansFeatureState = 'off' | 'researchPreview' | 'on';

export function parseFloorPlansFeatureState(value: string | null | undefined): FloorPlansFeatureState {
  if (!value) {
    return 'off';
  }

  const normalizedValue = value.trim().toLowerCase();

  if (normalizedValue === 'true' || normalizedValue === 'on') {
    return 'on';
  }

  if (normalizedValue === 'research-preview' || normalizedValue === 'research_preview' || normalizedValue === 'researchpreview') {
    return 'researchPreview';
  }

  return 'off';
}

@Injectable({
  providedIn: 'root',
})
export class RemoteConfigService {
  private injector = inject(Injector);
  private remoteConfig?: RemoteConfig;

  /** Fetched on app load; default used until then or if fetch fails. */
  public photos_maximum_external_drop_web = 20;

  /** Fetched on app load; default used until then or if fetch fails. */
  public report_disabled_categories = false;

  /** Fetched on app load; default used until then or if fetch fails. */
  public report_xlsx = false;

  /** Fetched on app load; default used until then or if fetch fails. */
  public themes_compact = false;

  /** Fetched on app load; default used until then or if fetch fails. */
  public floorPlansWebState = signal<FloorPlansFeatureState>('off');
  public floorPlansWebVisible = computed(() => this.floorPlansWebState() !== 'off');
  public floorPlansWebShowResearchPreviewBadge = computed(() => this.floorPlansWebState() === 'researchPreview');

  /**
   * Fetch and activate Remote Config, then populate values on this service.
   * Call from AppComponent (e.g. ngOnInit). Values are session-stable (no live updates).
   */
  public async init(): Promise<void> {
    const remoteConfig = await this.getRemoteConfig();
    if (!remoteConfig) {
      return;
    }

    return fetchAndActivate(remoteConfig)
      .then(() => this.applyFetchedValues())
      .catch((err) => {
        console.error('Error fetching remote config: ', err);
      });
  }

  private applyFetchedValues(): void {
    if (!this.remoteConfig) {
      return;
    }

    try {
      const photosVal = getValue(this.remoteConfig, KEY_PHOTOS_MAXIMUM_EXTERNAL_DROP_WEB).asNumber();
      this.photos_maximum_external_drop_web = photosVal || 20;

      this.report_disabled_categories = getValue(this.remoteConfig, KEY_REPORT_DISABLED_CATEGORIES).asBoolean();
      this.report_xlsx = getValue(this.remoteConfig, KEY_REPORT_XLSX).asBoolean();
      this.themes_compact = getValue(this.remoteConfig, KEY_THEMES_COMPACT).asBoolean();
      this.floorPlansWebState.set(parseFloorPlansFeatureState(getValue(this.remoteConfig, KEY_FLOOR_PLANS_WEB).asString()));
    } catch (err) {
      console.error('Error applying remote config values: ', err);
    }
  }

  private async getRemoteConfig(): Promise<RemoteConfig | null> {
    if (this.remoteConfig) {
      return this.remoteConfig;
    }

    const supported = await isSupported();
    if (!supported) {
      return null;
    }

    this.remoteConfig = this.injector.get(RemoteConfig);
    const remoteConfigMinimumFetchIntervalMillis = (environment as EnvironmentWithRemoteConfigOverride).remoteConfigMinimumFetchIntervalMillis;
    this.remoteConfig.settings = {
      minimumFetchIntervalMillis: remoteConfigMinimumFetchIntervalMillis ?? (environment.production ? REMOTE_CONFIG_PRODUCTION_FETCH_INTERVAL_MS : REMOTE_CONFIG_NON_PRODUCTION_FETCH_INTERVAL_MS),
      fetchTimeoutMillis: REMOTE_CONFIG_FETCH_TIMEOUT_MS,
    };
    return this.remoteConfig;
  }
}
